import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import IconButton from './IconButton';

/**
 * Cockpit SidePanel — the slide-over shell used for every create/edit
 * flow in the 2026 refresh (replaces centered modals).
 */
export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  eyebrow?: React.ReactNode;
  /** Panel width: px number (capped at 92vw), or any CSS width like '100vw' (uncapped). */
  width?: number | string;
  /** Extra stacking layer. Use 1+ when opening a panel from inside another panel. */
  layer?: number;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}

export default function SidePanel({ open, onClose, title, eyebrow, width = 420, layer = 0, children, footer }: SidePanelProps) {
  const overlayZ = 40 + layer * 20;
  const panelZ = overlayZ + 1;
  // Two-frame enter: when the panel mounts already-open (panels are often
  // conditionally rendered on first use), paint the closed position first
  // so the slide-in transition runs on the very first open too.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [open]);
  const shown = open && entered;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Portal to <body>: position:fixed breaks inside transformed/animated
  // ancestors (e.g. ck-stagger entry transforms), so the overlay must not
  // live in the page's layout tree.
  return createPortal(
    <div aria-hidden={!open} className="ck-overlay-root">
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: overlayZ,
          background: 'var(--surface-overlay)',
          opacity: shown ? 1 : 0,
          pointerEvents: shown ? 'auto' : 'none',
          transition: 'opacity var(--dur-slow) var(--ease-out)',
        }}
      />
      <section
        className="ck-sidepanel"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: panelZ,
          width: typeof width === 'number' ? width + 'px' : width,
          maxWidth: typeof width === 'number' ? '92vw' : '100vw',
          background: 'var(--surface-card)',
          borderLeft: '1px solid var(--border-hairline)',
          boxShadow: 'var(--shadow-pop)',
          transform: shown ? 'translateX(0)' : 'translateX(calc(100% + 24px))',
          transition: 'transform var(--dur-slow) var(--ease-out)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            // clear the notch when the panel is full-screen on a phone
            padding: 'calc(18px + var(--sat)) 22px 18px',
            borderBottom: '1px solid var(--border-hairline)',
            flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 0 }}>
            {eyebrow ? <div className="t-eyebrow" style={{ marginBottom: '4px' }}>{eyebrow}</div> : null}
            <div
              style={{
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
                letterSpacing: 'var(--tracking-tight)',
              }}
            >
              {title}
            </div>
          </div>
          <IconButton icon="x" label="Close" onClick={onClose} />
        </header>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            padding: footer ? '22px' : '22px 22px calc(22px + var(--sab))',
          }}
        >
          {children}
        </div>
        {footer ? (
          <footer
            style={{
              padding: '16px 22px calc(16px + var(--sab))',
              borderTop: '1px solid var(--border-hairline)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              flexShrink: 0,
            }}
          >
            {footer}
          </footer>
        ) : null}
      </section>
    </div>,
    document.body
  );
}
